import { FC, useState, useEffect } from 'react';
import { Box, Slider, Typography, SxProps } from '@mui/material';
import { useSearch, SEARCH_PARAM_RADIUS } from '@carcareers/contexts';

interface JobSearchRadiusInputProps {
  sx?: SxProps;
}

export const JobSearchRadiusInput: FC<JobSearchRadiusInputProps> = ({ sx }) => {
  const { radius: radiusContext, updateSearchParams } = useSearch();

  const [radius, setRadius] = useState<number>(parseInt(radiusContext, 10) || 151);

  const handleRadiusChange = (_event: Event, newValue: number | number[]) => {
    setRadius(newValue as number);
  };

  const handleChangeCommitted = (_event: React.SyntheticEvent | Event, newValue: number | number[]) => {
    const radiusValue = newValue as number;
    updateSearchParams(SEARCH_PARAM_RADIUS, radiusValue.toString());
  };

  const getRadiusLabel = (value: number) => {
    return value > 150 ? 'Any' : `${value} mi`;
  };

  useEffect(() => {
    const contextValue = parseInt(radiusContext, 10);
    if (contextValue !== radius) {
      setRadius(contextValue);
    }
  }, [radiusContext]);

  return (
    <Box sx={{ width: '100%', mt: { xs: 2, sm: 0 }, px: { xs: 3, sm: 0 }, ...sx }}>
      <Typography id="radius-slider" sx={{ mx: { xs: -2, sm: 0 } }} gutterBottom>
        Distance: {getRadiusLabel(radius)}
      </Typography>
      <Slider
        aria-labelledby="radius-slider"
        value={radius}
        onChange={handleRadiusChange}
        onChangeCommitted={handleChangeCommitted}
        min={10}
        max={160}
        step={10}
        marks={[{ value: 160, label: '150+' }]}
        valueLabelDisplay="auto"
        valueLabelFormat={getRadiusLabel}
        sx={{
          mb: 0,
          '& .MuiSlider-mark, & .MuiSlider-markLabel': {
            display: 'none',
          },
          '& .MuiSlider-rail': {
            color: 'grey.600',
          },
          '& .MuiSlider-thumb': {
            width: 25,
            height: 25,
            border: '4px solid',
            borderColor: 'background.paper',
            color: 'primary.main',
            '&:hover': {
              boxShadow: 'none',
            },
            '&:active': {
              boxShadow: 'none',
            },
            '&:focus': {
              boxShadow: 'none',
            },
            '&:focus-visible': {
              boxShadow: 'none',
            },
            '&:focus-within': {
              boxShadow: 'none',
            },
          },
        }}
      />
    </Box>
  );
};

export default JobSearchRadiusInput;
